import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

/**
 * Edit an add-on's reference price. Slug, name, description, limit_key, and
 * bundle_size are ALL immutable — the catalog is locked to the 4 canonical
 * add-ons. Sales can only nudge the unit price.
 */
export default function EditAddonDialog({ initial, onClose, onSaved }) {
  const bundleSize = Number(initial.bundle_size || 1);
  const isBundled = bundleSize > 1;
  const [unitPriceCents, setUnitPriceCents] = useState(initial.unit_price_cents ?? 0);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (unitPriceCents == null || unitPriceCents < 0) return toast.error("Unit price must be ≥ 0");
    setSaving(true);
    try {
      await api.patch(`/superadmin/subscription-addons/${initial.addon_id}`, {
        unit_price_cents: unitPriceCents,
      });
      toast.success("Add-on price updated");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const displayPrice = unitPriceCents == null ? "" : (unitPriceCents / 100).toFixed(2);
  const bundleUnitWord = initial.limit_key === "max_documents_per_project"
    ? "document"
    : initial.limit_key === "max_archived_projects"
    ? "project"
    : "unit";
  const priceUnitLabel = isBundled ? `per ${bundleSize}-${bundleUnitWord} bundle` : "per unit";

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md" data-testid="edit-addon-dialog">
        <DialogHeader>
          <DialogTitle>{initial.name}</DialogTitle>
          <DialogDescription>
            Update the reference price. Sales can still override it per firm from Manage subscription.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="rounded-2xl bg-[#F7F4EC] p-3 space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-[#7A7571]">Slug</span>
              <code className="font-mono text-[#2C2A29]">{initial.slug}</code>
            </div>
            <div className="flex justify-between">
              <span className="text-[#7A7571]">Increments</span>
              <span className="font-semibold text-[#2C2A29]">{initial.limit_key}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#7A7571]">Bundle size</span>
              <span className="font-semibold text-[#2C2A29]">
                {isBundled ? `${bundleSize} units per bundle` : "1 unit per unit"}
              </span>
            </div>
          </div>

          <label className="block">
            <div className="text-xs font-semibold text-[#4B4642] mb-1">Reference unit price ({priceUnitLabel})</div>
            <div className="rounded-2xl border border-[#EBE5D9] bg-white flex items-stretch">
              <span className="px-3 flex items-center text-[#7A7571] text-sm border-r border-[#EBE5D9] font-mono">{initial.currency}</span>
              <input
                type="number" min={0} step="0.01" required
                value={displayPrice}
                onChange={(e) => setUnitPriceCents(e.target.value === "" ? 0 : Math.round(Number(e.target.value) * 100))}
                data-testid="addon-field-unit-price"
                className="flex-1 px-3 py-2 bg-transparent focus:outline-none text-[#2C2A29]"
                placeholder="0.00"
                autoFocus
              />
            </div>
            {isBundled && (
              <div className="text-[11px] text-[#7A7571] mt-1">
                {initial.limit_key === "max_documents_per_project"
                  ? <>E.g. entering <span className="font-mono">10.00</span> = SGD 10 per {bundleSize} extra documents.</>
                  : initial.limit_key === "max_archived_projects"
                  ? <>E.g. entering <span className="font-mono">5.00</span> = SGD 5 per {bundleSize} extra archived projects.</>
                  : <>E.g. entering <span className="font-mono">5.00</span> = SGD 5 per {bundleSize}-unit bundle.</>}
              </div>
            )}
          </label>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-[#EBE5D9]">
            <button type="button" onClick={onClose} className="btn-ghost" data-testid="addon-cancel-btn">Cancel</button>
            <button
              type="submit"
              disabled={saving}
              data-testid="addon-save-btn"
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              Save price
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
